import { BlogPost } from '../types';

const post: BlogPost = {
	slug: 'comment-creer-un-livre-personnalise',
	title: 'Comment créer un livre-histoire personnalisé avec votre enfant en héros',
	description:
		'Un guide étape par étape pour créer un livre-histoire personnalisé : choisir une photo, sélectionner un style artistique, obtenir une ressemblance juste, et ce qu’il faut vérifier avant de valider l’impression.',
	date: '2026-02-03',
	heroImage: '/assets/hero/kid-3/watercolor_scene.png',
	tags: ['guide pratique', 'livres personnalisés'],
	lang: 'fr',
	dateLocale: 'fr-FR',
	body: `Créer un livre-histoire personnalisé est, dans l’ensemble, assez simple. Les points qui posent problème sont prévisibles, et presque tous remontent à la photo.

Voici le processus complet, et ce à quoi faire attention à chaque étape.

## 1. Choisir l’histoire en premier, pas le style artistique

L’histoire donne le ton. Une aventure nocturne et un sauvetage sous-marin ne conviennent pas aux mêmes enfants, et le style artistique doit suivre l’histoire, et non l’inverse.

Si vous achetez pour un enfant de moins de quatre ans, privilégiez des histoires plus courtes avec des scènes plus simples. Une douzaine de pages illustrées est le format idéal — assez long pour avoir un début et une fin, assez court pour être lu au coucher sans négociation.

## 2. Choisir la photo avec soin

C’est l’étape qui détermine tout ce qui suit. Une bonne photo produit un personnage qui ressemble indéniablement à votre enfant ; une mauvaise photo produit un personnage qui ressemble à un cousin.

**Ce qui fonctionne :**

- De face, ou presque
- Un éclairage doux et homogène — près d’une fenêtre, c’est idéal
- Le visage entier visible et net
- Une expression neutre ou un léger sourire

**Ce qui pose problème :**

- Chapeaux, lunettes de soleil, ou cheveux devant le visage
- Des ombres marquées, en particulier dues à un soleil au zénith
- Des angles extrêmes — regard nettement vers le haut ou vers le bas
- Des filtres appuyés, un lissage « beauté », ou des photos de téléphone prises en faible lumière avec une réduction de bruit agressive
- Des photos de groupe où le visage de l’enfant occupe une petite partie du cadre

Une photo professionnelle n’est pas nécessaire. Une photo de téléphone prise en intérieur, un jour nuageux, est généralement meilleure qu’une photo en extérieur en plein soleil, parce que la lumière y est plus uniforme.

## 3. Choisir un style artistique

Chaque style change la façon dont la ressemblance se perçoit :

- **Aquarelle** — doux, traditionnel, esprit souvenir. Magnifique, mais la ressemblance reste impressionniste plutôt qu’exacte.
- **Livre d’histoire classique** — chaleureux et détaillé, le style le plus proche des livres illustrés dont la plupart des adultes gardent le souvenir.
- **Moderne aplat** — formes franches et couleurs vives. Se prête particulièrement bien aux tout jeunes enfants.
- **Bande dessinée** — en cases, énergique. Convient bien aux enfants plus âgés.
- **3D façon Pixar** — brillant et rendu en volume. Généralement la ressemblance la plus immédiatement reconnaissable, et celle que les enfants choisissent quand on leur laisse le choix.

Si le livre est avant tout un cadeau pour l’enfant, privilégiez la reconnaissance. S’il s’agit avant tout d’un souvenir pour vous, l’aquarelle et le style classique vieillissent avec plus d’élégance.

## 4. Vérifier le personnage avant la construction du livre

Le personnage est généré en premier, avant les scènes individuelles. C’est le moment le moins coûteux pour repérer un problème.

Regardez-le et posez-vous une seule question : *un grand-parent reconnaîtrait-il cet enfant ?*

Si la réponse est non, essayez une autre photo avant de continuer. Corriger le personnage maintenant est bien plus rapide que de relire douze pages construites sur une ressemblance qui n’a jamais été la bonne.

## 5. Relire chaque page

Une fois les scènes générées, examinez-les vraiment, sans les survoler.

Ce qui mérite d’être vérifié :

- **La cohérence.** Le personnage a-t-il l’air du même enfant sur chaque scène ? Les enfants remarquent les incohérences plus vite que les adultes, et cela brise l’illusion immédiatement.
- **Les mains et les petits détails.** C’est historiquement là où l’illustration générée peine le plus.
- **Le placement du texte.** Vérifiez qu’aucun élément important de l’illustration n’est masqué.
- **La couverture.** C’est la page la plus regardée de toutes.

Toute page qui ne convient pas peut être régénérée. Faites-le avant de valider — rien n’est imprimé tant que vous n’avez pas donné votre accord, et c’est précisément l’intérêt de cette étape.

## 6. Valider et imprimer

Une fois la validation donnée, le livre part à l’impression puis est expédié. Prévoyez du temps : deux à trois semaines entre la commande et la réception est une estimation confortable, davantage en décembre.

## Une remarque sur les langues

Si vous produisez le livre dans une langue qui s’écrit de droite à gauche, comme l’arabe, la mise en page doit être véritablement inversée — ordre des pages, alignement du texte et placement des illustrations — et non simplement traduite dans un gabarit conçu de gauche à droite. Un livre en écriture droite-à-gauche paginé de gauche à droite se lit à l’envers, ce qui saute aux yeux de tout lecteur natif, et qui est facile à manquer si l’on n’en est pas un.

## Erreurs courantes, en condensé

1. Choisir la plus belle photo plutôt que la plus nette. La plus belle photo est souvent à contre-jour au coucher du soleil. C’est le pire des cas.
2. Valider sans avoir regardé chaque page.
3. Commander quatre jours avant l’anniversaire.
4. Choisir l’aquarelle pour un enfant de deux ans parce que l’adulte préfère ce style.

Aucune de ces erreurs n’est fatale, mais toutes sont évitables.`,
	faqs: [
		{
			question: 'Quel type de photo est le meilleur pour un livre-histoire personnalisé ?',
			answer:
				'Une photo nette, prise de face, avec un éclairage doux et homogène, où le visage entier est visible et net. La lumière intérieure près d’une fenêtre fonctionne généralement mieux que le plein soleil en extérieur, qui crée des ombres marquées. Évitez les chapeaux, les lunettes de soleil, les angles extrêmes et les filtres appuyés.',
		},
		{
			question: 'Puis-je modifier les illustrations avant l’impression du livre ?',
			answer:
				'Oui. Le personnage est généré en premier afin que vous puissiez vérifier la ressemblance dès le début, et chaque page est relue avant l’impression. Les pages individuelles peuvent être régénérées si elles ne conviennent pas.',
		},
		{
			question: 'Quelle doit être la longueur d’un livre-histoire personnalisé ?',
			answer:
				'Une douzaine de pages illustrées convient à la plupart des enfants de moins de huit ans — assez long pour une histoire complète, assez court pour être lu d’une traite au coucher.',
		},
		{
			question: 'Quel style artistique donne la meilleure ressemblance ?',
			answer:
				'Le style 3D façon Pixar et l’illustration moderne aplat donnent généralement la ressemblance la plus immédiatement reconnaissable. Les styles aquarelle et livre d’histoire classique sont plus doux et plus impressionnistes, ce que beaucoup de parents préfèrent pour un souvenir.',
		},
	],
	ui: {
		homeLabel: 'Accueil',
		blogLabel: 'Blog',
		faqHeading: 'Questions fréquentes',
		moreReadingHeading: 'À lire aussi',
		ctaLabel: 'Découvrir les cadeaux personnalisés',
	},
};

export default post;
